import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_5bd73245 from 'nuxt_plugin_sentryserver_5bd73245' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_13c47e86 from 'nuxt_plugin_sentryclient_13c47e86' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_bootstrap_68fdc73f from 'nuxt_plugin_bootstrap_68fdc73f' // Source: ../plugins/bootstrap.js (mode: 'client')
import nuxt_plugin_errorhandler_be094f82 from 'nuxt_plugin_errorhandler_be094f82' // Source: ../plugins/errorhandler.js (mode: 'client')
import nuxt_plugin_tabid_84e66406 from 'nuxt_plugin_tabid_84e66406' // Source: ../plugins/tabid.js (mode: 'client')
import nuxt_plugin_eventbus_3db4fd4a from 'nuxt_plugin_eventbus_3db4fd4a' // Source: ../plugins/eventbus.js (mode: 'all')
import nuxt_plugin_filters_2dd71012 from 'nuxt_plugin_filters_2dd71012' // Source: ../plugins/filters.js (mode: 'all')
import nuxt_plugin_i18n_1fba523a from 'nuxt_plugin_i18n_1fba523a' // Source: ../plugins/i18n.js (mode: 'all')
import nuxt_plugin_vuejsmodal_a01fef9e from 'nuxt_plugin_vuejsmodal_a01fef9e' // Source: ../plugins/vue-js-modal.js (mode: 'all')
import nuxt_plugin_super_e14bcbdc from 'nuxt_plugin_super_e14bcbdc' // Source: ../plugins/super.js (mode: 'all')
import nuxt_plugin_vuematomo_4825ec29 from 'nuxt_plugin_vuematomo_4825ec29' // Source: ../plugins/vue-matomo.js (mode: 'client')
import nuxt_plugin_vueuploadcomponent_2e809929 from 'nuxt_plugin_vueuploadcomponent_2e809929' // Source: ../plugins/vue-upload-component.js (mode: 'client')
import nuxt_plugin_ckeditor4_3ae1450e from 'nuxt_plugin_ckeditor4_3ae1450e' // Source: ../plugins/ckeditor4.js (mode: 'client')
import nuxt_plugin_socket_4695eaee from 'nuxt_plugin_socket_4695eaee' // Source: ../plugins/socket.js (mode: 'client')
import nuxt_plugin_vueselect_af0f7784 from 'nuxt_plugin_vueselect_af0f7784' // Source: ../plugins/vue-select.js (mode: 'client')
import nuxt_plugin_vueselectize_69172c7c from 'nuxt_plugin_vueselectize_69172c7c' // Source: ../plugins/vue-selectize.js (mode: 'client')
import nuxt_plugin_vue100vh_dc9e5cde from 'nuxt_plugin_vue100vh_dc9e5cde' // Source: ../plugins/vue-100vh.js (mode: 'client')
import nuxt_plugin_notification_0f206214 from 'nuxt_plugin_notification_0f206214' // Source: ../plugins/notification.js (mode: 'client')
import nuxt_plugin_bootstrapdatepicker_f9f84d34 from 'nuxt_plugin_bootstrapdatepicker_f9f84d34' // Source: ../plugins/bootstrap-datepicker.js (mode: 'client')
import nuxt_plugin_vcalendar_0665e239 from 'nuxt_plugin_vcalendar_0665e239' // Source: ../plugins/vcalendar.js (mode: 'client')
import nuxt_plugin_vuetelinput_3f647bbc from 'nuxt_plugin_vuetelinput_3f647bbc' // Source: ../plugins/vue-tel-input.js (mode: 'client')
import nuxt_plugin_vueclipboard_03d9e0fe from 'nuxt_plugin_vueclipboard_03d9e0fe' // Source: ../plugins/vue-clipboard.js (mode: 'client')
import nuxt_plugin_googlemapsapi_7e1d8c16 from 'nuxt_plugin_googlemapsapi_7e1d8c16' // Source: ../plugins/google-maps-api.js (mode: 'client')
import nuxt_plugin_jquerymatchheight_53b29b2f from 'nuxt_plugin_jquerymatchheight_53b29b2f' // Source: ../plugins/jquery-match-height.js (mode: 'client')
import nuxt_plugin_cypress_69effd80 from 'nuxt_plugin_cypress_69effd80' // Source: ../plugins/cypress.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    return this.$root.$options.$nuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule
const baseStoreOptions = { preserveState: process.client }

function registerModule (path, rawModule, options = {}) {
  return originalRegisterModule.call(this, path, rawModule, { ...baseStoreOptions, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Johku","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"Johku"},{"http-equiv":"X-UA-Compatible","content":"IE=edge,chrome=1"},{"name":"apple-mobile-web-app-capable","content":"yes"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"},{"rel":"apple-touch-icon","href":"~\u002Fassets\u002Fimg\u002Fapple-touch-icon.png"},{"rel":"stylesheet","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss?family=Oxygen:300,400,700"}],"script":[{"src":"https:\u002F\u002Fapis.google.com\u002Fjs\u002Fapi.js"}],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_5bd73245 === 'function') {
    await nuxt_plugin_sentryserver_5bd73245(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_13c47e86 === 'function') {
    await nuxt_plugin_sentryclient_13c47e86(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_bootstrap_68fdc73f === 'function') {
    await nuxt_plugin_bootstrap_68fdc73f(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_errorhandler_be094f82 === 'function') {
    await nuxt_plugin_errorhandler_be094f82(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_tabid_84e66406 === 'function') {
    await nuxt_plugin_tabid_84e66406(app.context, inject)
  }

  if (typeof nuxt_plugin_eventbus_3db4fd4a === 'function') {
    await nuxt_plugin_eventbus_3db4fd4a(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_2dd71012 === 'function') {
    await nuxt_plugin_filters_2dd71012(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_1fba523a === 'function') {
    await nuxt_plugin_i18n_1fba523a(app.context, inject)
  }

  if (typeof nuxt_plugin_vuejsmodal_a01fef9e === 'function') {
    await nuxt_plugin_vuejsmodal_a01fef9e(app.context, inject)
  }

  if (typeof nuxt_plugin_super_e14bcbdc === 'function') {
    await nuxt_plugin_super_e14bcbdc(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuematomo_4825ec29 === 'function') {
    await nuxt_plugin_vuematomo_4825ec29(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueuploadcomponent_2e809929 === 'function') {
    await nuxt_plugin_vueuploadcomponent_2e809929(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ckeditor4_3ae1450e === 'function') {
    await nuxt_plugin_ckeditor4_3ae1450e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_socket_4695eaee === 'function') {
    await nuxt_plugin_socket_4695eaee(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueselect_af0f7784 === 'function') {
    await nuxt_plugin_vueselect_af0f7784(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueselectize_69172c7c === 'function') {
    await nuxt_plugin_vueselectize_69172c7c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vue100vh_dc9e5cde === 'function') {
    await nuxt_plugin_vue100vh_dc9e5cde(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_notification_0f206214 === 'function') {
    await nuxt_plugin_notification_0f206214(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_bootstrapdatepicker_f9f84d34 === 'function') {
    await nuxt_plugin_bootstrapdatepicker_f9f84d34(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vcalendar_0665e239 === 'function') {
    await nuxt_plugin_vcalendar_0665e239(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuetelinput_3f647bbc === 'function') {
    await nuxt_plugin_vuetelinput_3f647bbc(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueclipboard_03d9e0fe === 'function') {
    await nuxt_plugin_vueclipboard_03d9e0fe(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googlemapsapi_7e1d8c16 === 'function') {
    await nuxt_plugin_googlemapsapi_7e1d8c16(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_jquerymatchheight_53b29b2f === 'function') {
    await nuxt_plugin_jquerymatchheight_53b29b2f(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_cypress_69effd80 === 'function') {
    await nuxt_plugin_cypress_69effd80(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, (err) => {
        // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
        if (!err._isRouter) return reject(err)
        if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
